import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Box, Preloader, useResponsive } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { ContinueInAppPopup } from '@/modules/core/components/ContinueInAppPopup';
import { SIGN_IN_STEP_PARAM_KEY, STEPS } from '@/modules/user/components/SignIn/signIn.types';
import SignInWrapper from '@/modules/user/components/SignIn/SignInWrapper';

import useHandleResetPasswordLink from './hooks/useHandleResetPasswordLink';
import useValidateResetLink from './hooks/useValidateResetLink';
import { EnterNewPassword, ResetPasswordError, ResetPasswordSuccess } from './components';
import { ResetPasswordStep } from './resetPassword.types';

const getAppLink = (linkId: string, code: string) => `elevate://open-app?method=resetPassword&link-id=${linkId}&code=${encodeURIComponent(code)}`;

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const history = useHistory();
  const [step, setStep] = useState<ResetPasswordStep>(ResetPasswordStep.LOADING);
  const [isAppPopupVisible, setIsAppPopupVisible] = useState(true);

  const { validateResetLink } = useValidateResetLink({ setStep });
  const { code, linkId } = useHandleResetPasswordLink({ onSuccess: validateResetLink, setStep });

  const verificationAppLinks = useMemo(() => ({
    defaultAppLink: getAppLink(linkId, code),
  }), [code, linkId]);

  const handleSuccess = useCallback(() => {
    setStep(ResetPasswordStep.PASSWORD_UPDATED);
  }, []);

  const handleRequestNewLink = useCallback(() => {
    history.push(`${ROUTES.LOGIN}?${SIGN_IN_STEP_PARAM_KEY}=${STEPS.FORGOT_PASSWORD}`);
  }, [history]);

  const handleGoToLogin = useCallback(() => {
    history.push(ROUTES.LOGIN);
  }, [history]);

  return (
    <SignInWrapper
      isBackButton={step === ResetPasswordStep.RESET_PASSWORD}
      onBack={handleGoToLogin}
      isTitleLess={isMobile}
      formWrapperProps={{ width: { width: '100%', max: '548px' }}}
    >
      <Box
        width={{ width: '100%', max: '400px' }}
        margin={{ top: '-24px'}}
        data-testid="reset-password_wrapper"
      >
        {step === ResetPasswordStep.LOADING && <Preloader data-testid="reset-password_loading" />}
        {step === ResetPasswordStep.RESET_PASSWORD && (
          <>
            <EnterNewPassword
              linkId={linkId}
              code={code}
              onSuccess={handleSuccess}
            />
            <ContinueInAppPopup
              visible={isAppPopupVisible}
              setVisible={setIsAppPopupVisible}
              appLinks={verificationAppLinks}
            />
          </>
        )}
        {step === ResetPasswordStep.LINK_EXPIRED && (
          <ResetPasswordError
            testId="link-expired"
            title={t('Link Expired')}
            subTitle={t('This link is no longer valid. Password reset links expire after 1 hour for security reasons.')}
            buttonTitle={t('Request New Link')}
            onRequestNewLink={handleRequestNewLink}
          />
        )}
        {step === ResetPasswordStep.LINK_INVALID && (
          <ResetPasswordError
            testId="link-invalid"
            title={t('Link Invalid')}
            subTitle={t('We couldn\'t verify this link. It may have been copied incorrectly or is no longer active.')}
            buttonTitle={t('Request New Link')}
            onRequestNewLink={handleRequestNewLink}
          />
        )}
        {step === ResetPasswordStep.PASSWORD_UPDATED && (
          <ResetPasswordSuccess onGoToLogin={handleGoToLogin} />
        )}
      </Box>
    </SignInWrapper>
  );
};

export default ResetPasswordPage;
